import { createSelector } from 'reselect'
import filter from 'lodash/filter'
import map from 'lodash/map'
import flow from 'lodash/flow'
import curryRight from 'lodash/curryRight'
import { candidateProfileRolesHelper } from '@offerzen/pinata-shame'
import ChevronLeftRightIcon from '@offerzen/zenicon/ChevronLeftRightIcon'
import SocialBehanceIcon from '@offerzen/zenicon/SocialBehanceIcon'
import SocialBitbucketIcon from '@offerzen/zenicon/SocialBitbucketIcon'
import SocialBlogIcon from '@offerzen/zenicon/SocialBlogIcon'
import SocialDribbbleIcon from '@offerzen/zenicon/SocialDribbbleIcon'
import SocialGithubIcon from '@offerzen/zenicon/SocialGithubIcon'
import SocialLinkedinIcon from '@offerzen/zenicon/SocialLinkedinIcon'
import WebsiteIcon from '@offerzen/zenicon/WebsiteIcon'

import { candidateFirstWeekInBatch, candidateLastWeekInBatch } from '@/core/helpers/batchStatusHelpers'
import { getLocation } from '@/core/selectors/router'

import { computeEducation } from '../../helpers/candidateDetailHelpers'

import { getPositions } from './positions'

const curriedMap = curryRight(map)
const curriedFilter = curryRight(filter)

const hasProfileUrl = profile => profile.url

export const candidateSelector = state => state.views.candidateDetails_LEGACY.candidate.member
const candidateUtils = state => state.views.candidateDetails_LEGACY.candidate.utils
const statics = state => state.static_data

export const candidateCitizenship = createSelector([candidateSelector], subState => subState.citizenship_south_africa)
export const candidateFirstName = createSelector([candidateSelector], candidate => candidate.name.split(' ')[0])

const onlineProfileIcon = {
  github: {
    icon: SocialGithubIcon,
    size: '18px',
  },
  linkedin: {
    icon: SocialLinkedinIcon,
  },
  dribbble: {
    icon: SocialDribbbleIcon,
  },
  behance: {
    icon: SocialBehanceIcon,
  },
  bitbucket: {
    icon: SocialBitbucketIcon,
    size: '20px',
  },
  blog: {
    icon: SocialBlogIcon,
  },
  personal_site: {
    icon: WebsiteIcon,
  },
}
export const candidateDetails = createSelector([candidateSelector, candidateFirstName], (candidate, firstName) => {
  const roles = candidateProfileRolesHelper(candidate.experience)

  const formatSkills = skills => {
    if (skills) {
      return skills.split(',').map(skill => ({ name: skill, key: skill.toLowerCase().replace(/ /g, '_') }))
    }
    return []
  }

  const isCandidateLive = candidate.current_state === 'live'

  return {
    ...candidate,
    firstName,
    recentlyAdded: isCandidateLive && candidateFirstWeekInBatch(candidate.transitioned_at),
    lastWeekInBatch: isCandidateLive && candidateLastWeekInBatch(candidate.transitioned_at),
    education: computeEducation(candidate.education),
    projects: candidate.projects.map(project => ({
      title: project.name,
      description: project.description,
      image: project.file_url,
    })),
    skills: formatSkills(candidate.skills),
    experience: roles,
    online_profiles: flow(
      curriedFilter(hasProfileUrl),
      curriedMap(profile => ({
        ...profile,
        url: !profile.url.startsWith('http') ? `http://${profile.url}` : profile.url,
        icon: onlineProfileIcon[profile.type]?.icon || ChevronLeftRightIcon,
        iconSize: onlineProfileIcon[profile.type]?.size || '16px',
      })),
    )(candidate.online_profiles),
    interview_request: candidate.interview_request,
    summary: candidate.summary || '',
  }
})

export const mustStillFetch = createSelector(candidateUtils, candidateUtils => !candidateUtils.isFetching || !candidateUtils.hasFetched)

export const hasFetched = createSelector(candidateUtils, candidateUtils => candidateUtils.hasFetched)

export const isFetching = createSelector(candidateUtils, candidateUtils => candidateUtils.isFetching)

export const fetchError = createSelector(candidateUtils, candidateUtils => candidateUtils.hasFetchError)

export const getMember = createSelector(statics, statics => statics.current_user)

export const getAccountManager = createSelector(statics, statics => statics.company.account_manager)

const getFavourites = createSelector(candidateSelector, candidate => candidate.likes)

export const getFavouriteByCurrentMember = createSelector(
  getMember,
  getAccountManager,
  getFavourites,
  (member, accountManager, favouritesHash) => {
    const memberId = member.is_account_manager ? accountManager.id : member.member_id
    const currentHiringTeamId = member.current_hiring_team_id

    // check if a member has a favourite and if that favourite is for the correct hiring team
    return favouritesHash[memberId] && favouritesHash[memberId].hiring_team_id === currentHiringTeamId ? favouritesHash[memberId] : null
  },
)

export const getLikeId = createSelector(getFavouriteByCurrentMember, favourite => (favourite ? favourite.like_id : null))

export const hasFavourite = createSelector(getFavouriteByCurrentMember, memberFav => !!memberFav)

export const favouriteHasPositionId = createSelector(getFavouriteByCurrentMember, fav => (fav ? fav.job_opening_id : undefined))

export const positionListForFavourites = createSelector([getPositions], positions =>
  positions.map(({ id, name }) => ({
    id,
    name,
  })),
)

export const isLastListRecommendations = createSelector([getLocation], location => {
  return location?.state?.url?.includes('recommendations')
})

export const lastListName = createSelector([getLocation], location => {
  const previousLocation = location?.state?.url?.split('/').pop()?.split('?')[0]

  // when going to candidate profile, the state is saved as the list you came from
  switch (previousLocation) {
    case 'shortlist':
      return 'Favourites list'
    case 'dashboard':
      return 'Dashboard'
    case 'applications':
      return 'Applications'
    case 'recommendations':
      return 'Recommendations'
    default:
      return 'Search'
  }
})
